// components/auth/TwoFactorModal.jsx
'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import useApiError from '@/hooks/useApiError';

import { logger } from '@/utils/log';
export default function TwoFactorModal({ isOpen, onClose, method, availableMethods }) {
    const router = useRouter();
    const { submitTwoFactorCode, resendTwoFactorCode } = useAuth();
    const handleError = useApiError();
    const [code, setCode] = useState('');
    // Where the code was sent (legacy TwoFactorMethod) and where a resend
    // should go (legacy RetryMethod) are two values: the notice keeps naming
    // the method the last code actually went to until a resend succeeds.
    const [sentTo, setSentTo] = useState(method);
    const [selectedMethod, setSelectedMethod] = useState(method);
    const [methods, setMethods] = useState(availableMethods || []);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (method) {
            setSentTo(method);
            setSelectedMethod(method);
        }
    }, [method]);

    useEffect(() => {
        setMethods(availableMethods || []);
    }, [availableMethods]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await submitTwoFactorCode(code);

            if (result.success) {
                onClose();
                router.push('/');
            } else {
                setError(result.error || 'Invalid code');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            logger.error('2FA error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Legacy Login.razor.cs ResendAuthentication (L505-551): re-post to
    // resendtwofactorcode with the selected method, adopt the new key and
    // method, then "Verification code resent". Failures go through the
    // project's error handler (toast + error log).
    const handleResend = async () => {
        setError('');
        setLoading(true);
        try {
            const result = await resendTwoFactorCode(selectedMethod);
            if (result.success) {
                if (result.twoFactorMethod) {
                    setSentTo(result.twoFactorMethod);
                    setSelectedMethod(result.twoFactorMethod);
                }
                if (Array.isArray(result.availableMethods) && result.availableMethods.length > 0) {
                    setMethods(result.availableMethods);
                }
                // Legacy toastService.ShowInfo — the project's info-toast idiom.
                toast('Verification code resent', { icon: 'ℹ️' });
            } else {
                handleError(result.response || result.error, { action: 'resendTwoFactorCode' });
            }
        } catch (err) {
            handleError(err, { action: 'resendTwoFactorCode' });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto">
            <div className="modal-content animate-scale-in w-full max-w-md" onClick={e => e.stopPropagation()}>

                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200">
                    <h4 className="text-gray-900">Two-Factor Authentication Required</h4>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost btn-sm p-1.5 rounded-lg"
                        disabled={loading}
                        aria-label="Close"
                    >
                        <FaTimes className="w-4 h-4" />
                    </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit}>
                    <div className="p-4 sm:p-5 space-y-4">
                        <p className="text-sm text-gray-600">
                            A verification code has been sent to {sentTo}. Enter your one-time code below:
                        </p>

                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="Enter code here"
                            className="input-field"
                            maxLength={14}
                            required
                            disabled={loading}
                        />

                        {/* Always rendered, even with a single method — legacy
                            Login.razor L65-97 shows the select unconditionally
                            (audit 12t item 10). The leading blank option is
                            legacy's too. */}
                        <div className="space-y-2">
                            <p className="text-sm text-gray-600">Resend to selected method below:</p>
                            <select
                                name="methods"
                                id="selectedMethod"
                                aria-label="Resend to selected method"
                                value={selectedMethod || ''}
                                onChange={(e) => setSelectedMethod(e.target.value)}
                                className="input-field"
                                disabled={loading}
                            >
                                <option value="" disabled></option>
                                {methods.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </select>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className="flex gap-3 p-4 sm:p-5 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 btn btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={loading || !selectedMethod}
                            className="flex-1 btn btn-outline"
                        >
                            Resend
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 btn btn-primary"
                        >
                            {loading ? <><FaSpinner className="animate-spin w-3.5 h-3.5" /> Verifying…</> : 'Submit Code'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
